/**
 * Format-string tokens recognised by `dateFormatToMask` and the round-
 * trip helpers. ADR-0076.
 */
const TOKENS = ['YYYY', 'MM', 'DD'] as const;
type DateToken = (typeof TOKENS)[number];

const TOKEN_MASK: Record<DateToken, string> = {
  YYYY: '9999',
  MM: '99',
  DD: '99',
};

/**
 * Build a `react-native-mask-text` mask string from a date format like
 * `YYYY-MM-DD` / `DD/MM/YYYY`. Each YYYY becomes 9999, MM/DD become
 * 99, separators pass through.
 *
 * Unsupported tokens (e.g. `MMM`, `DDDD`) pass through as literals —
 * the input still works, just with no mask validation on those chars.
 */
export function dateFormatToMask(format: string): string {
  let out = format;
  for (const token of TOKENS) {
    out = out.split(token).join(TOKEN_MASK[token]);
  }
  return out;
}

interface SegmentPositions {
  yearStart: number;
  monthStart: number;
  dayStart: number;
}

function locateSegments(format: string): SegmentPositions | null {
  const y = format.indexOf('YYYY');
  const m = format.indexOf('MM');
  const d = format.indexOf('DD');
  if (y < 0 || m < 0 || d < 0) return null;
  return { yearStart: y, monthStart: m, dayStart: d };
}

function isValidYmd(y: number, m: number, d: number): boolean {
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return false;
  if (m < 1 || m > 12) return false;
  if (d < 1 || d > 31) return false;
  // Compare round-trip through Date — guards against Feb-30 etc.
  const probe = new Date(Date.UTC(y, m - 1, d));
  return probe.getUTCFullYear() === y && probe.getUTCMonth() + 1 === m && probe.getUTCDate() === d;
}

/**
 * Convert an ISO date string (`YYYY-MM-DD`) into the display format.
 * Returns the input unchanged when it doesn't parse — callers display
 * the raw value so the user can still see what they typed.
 */
export function isoToDisplay(iso: string, format: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const [, y, mo, d] = m;
  if (y === undefined || mo === undefined || d === undefined) return iso;
  if (!isValidYmd(Number(y), Number(mo), Number(d))) return iso;
  return format
    .replace('YYYY', y.padStart(4, '0'))
    .replace('MM', mo.padStart(2, '0'))
    .replace('DD', d.padStart(2, '0'));
}

/**
 * Convert a display-format string back to ISO (`YYYY-MM-DD`). Returns
 * the input unchanged when the segments can't be located. Returns the
 * assembled ISO even if it's not a valid calendar date — the caller
 * can still emit it; downstream validation handles the malformed case.
 */
export function displayToIso(display: string, format: string): string {
  const positions = locateSegments(format);
  if (!positions) return display;
  const y = display.slice(positions.yearStart, positions.yearStart + 4);
  const m = display.slice(positions.monthStart, positions.monthStart + 2);
  const d = display.slice(positions.dayStart, positions.dayStart + 2);
  if (!/^\d{4}$/.test(y) || !/^\d{2}$/.test(m) || !/^\d{2}$/.test(d)) return display;
  return `${y}-${m}-${d}`;
}
